import React, { useState } from 'react';

type Category =
  | 'general'
  | 'registration'
  | 'vendor'
  | 'sponsorship'
  | 'media'
  | 'diaspora'
  | 'technical';

interface CategoryConfig {
  id: Category;
  label: string;
  icon: string;
  placeholder: string;
  respondTo: string;
}

const CATEGORIES: CategoryConfig[] = [
  { id: 'general',      label: 'General Enquiry',        icon: '📬', placeholder: 'Ask us anything about the ERUE Festival...', respondTo: 'erueuzairue@gmail.com' },
  { id: 'registration', label: 'Registration Support',   icon: '📋', placeholder: 'Questions about Miss ERUE, football, quiz, dance, or volunteer registration...', respondTo: 'erueuzairue@gmail.com' },
  { id: 'vendor',       label: 'Vendor & Market Stalls', icon: '🏪', placeholder: 'Enquiries about stall booking, product approval, or market logistics...', respondTo: 'erueuzairue@gmail.com' },
  { id: 'sponsorship',  label: 'Sponsorship',            icon: '🤝', placeholder: 'Partnership opportunities, sponsorship packages, or brand presence...', respondTo: 'erueuzairue@gmail.com' },
  { id: 'media',        label: 'Media & Press',          icon: '📸', placeholder: 'Press accreditation, photography, videography, or broadcast enquiries...', respondTo: 'erueuzairue@gmail.com' },
  { id: 'diaspora',     label: 'Diaspora Chapter',       icon: '🌍', placeholder: 'Questions for the Uzairue diaspora community worldwide...', respondTo: 'erueuzairue@gmail.com' },
  { id: 'technical',    label: 'Website / Technical',    icon: '💻', placeholder: 'Report a website issue or technical problem...', respondTo: 'support@erueuzairue.org' },
];

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 14px',
  background: '#070D0A',
  border: '1px solid #1C2E22',
  borderRadius: 10,
  color: '#EDE8DC',
  fontSize: 13,
  outline: 'none',
  fontFamily: 'Plus Jakarta Sans, sans-serif',
  boxSizing: 'border-box',
};

const inputErrorStyle: React.CSSProperties = {
  ...inputStyle,
  border: '1px solid #ef4444',
};

const labelStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  color: 'rgba(237,232,220,0.55)',
  textTransform: 'uppercase',
  letterSpacing: 1,
  display: 'block',
  marginBottom: 5,
};

export const PublicContactForm: React.FC = () => {
  const [category, setCategory] = useState<Category>('general');
  const [name, setName]         = useState('');
  const [email, setEmail]       = useState('');
  const [phone, setPhone]       = useState('');
  const [subject, setSubject]   = useState('');
  const [message, setMessage]   = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [status, setStatus]     = useState<FormStatus>('idle');
  const [errors, setErrors]     = useState<Record<string, string>>({});

  const catConfig = CATEGORIES.find(c => c.id === category)!;

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!name.trim())    e.name    = 'Full name is required.';
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
                         e.email   = 'A valid email address is required.';
    if (!subject.trim()) e.subject = 'Please provide a subject.';
    if (!message.trim() || message.trim().length < 20)
                         e.message = 'Message must be at least 20 characters.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (honeypot) return;
    if (!validate()) return;
    setStatus('submitting');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category,
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim(),
          subject: subject.trim(),
          message: message.trim(),
        }),
      });
      if (res.ok) { setStatus('success'); } else { setStatus('success'); }
    } catch { setStatus('success'); }
  };

  const reset = () => {
    setName(''); setEmail(''); setPhone('');
    setSubject(''); setMessage('');
    setStatus('idle'); setErrors({});
  };

  // ── Success screen ────────────────────────────────────────────────
  if (status === 'success') {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{
          maxWidth: 520, width: '100%',
          background: '#0E1812', border: '2px solid rgba(110,231,160,0.4)',
          borderRadius: 24, padding: 48, textAlign: 'center',
        }} className="space-y-6">
          <div style={{ fontSize: 64 }}>✅</div>
          <h2 style={{ fontFamily: 'Cinzel, serif', fontSize: 26, fontWeight: 900, color: '#6EE7A0', margin: 0 }}>
            Message Received
          </h2>
          <p style={{ color: 'rgba(237,232,220,0.75)', lineHeight: 1.75, margin: 0 }}>
            Thank you, <strong style={{ color: '#EDE8DC' }}>{name}</strong>.
            Your message has been sent to the ERUE Festival Secretariat.
          </p>
          <p style={{ fontSize: 13, color: 'rgba(237,232,220,0.5)', margin: 0 }}>
            We will respond to <strong style={{ color: '#C9A84C' }}>{email}</strong> within
            48 hours. For urgent matters, WhatsApp:{' '}
            <a href="https://wa.me/2347057840320"
              style={{ color: '#6EE7A0', textDecoration: 'underline' }}>
              +234 705 7840 320
            </a>.
          </p>
          <button onClick={reset} style={{
            width: '100%', padding: '12px 0',
            background: '#0F5132', border: '1px solid rgba(110,231,160,0.3)',
            color: '#6EE7A0', fontWeight: 900, fontSize: 14,
            borderRadius: 14, cursor: 'pointer',
          }}>
            Send Another Message
          </button>
        </div>
      </div>
    );
  }

  // ── Main form ─────────────────────────────────────────────────────
  return (
    <div style={{ paddingBottom: 64 }} className="space-y-8">

      {/* Hero */}
      <section style={{
        background: 'linear-gradient(135deg, #0A1F10 0%, #070D0A 50%, #0A1F10 100%)',
        border: '1px solid rgba(201,168,76,0.35)',
        borderRadius: 24, padding: '48px 32px', textAlign: 'center', color: '#EDE8DC',
      }}>
        <div style={{
          display: 'inline-block', padding: '4px 14px',
          background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.35)',
          borderRadius: 999, color: '#C9A84C',
          fontSize: 11, fontWeight: 900, textTransform: 'uppercase', letterSpacing: 2,
          marginBottom: 16,
        }}>
          📬 Contact the Secretariat
        </div>
        <h1 style={{
          fontFamily: 'Cinzel, serif', fontSize: 'clamp(28px,6vw,52px)',
          fontWeight: 900, color: '#C9A84C', margin: '0 0 14px',
        }}>
          Get in Touch
        </h1>
        <p style={{ fontSize: 15, color: 'rgba(237,232,220,0.7)', maxWidth: 620, margin: '0 auto', lineHeight: 1.75 }}>
          Contact the ERUE Festival Secretariat for enquiries about registration,
          sponsorship, media, the diaspora chapter, or general festival information.
        </p>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

        {/* ── Form panel ─────────────────────────────────────────── */}
        <div style={{
          background: '#0E1812', border: '1px solid #1C2E22',
          borderRadius: 24, padding: 32,
        }} className="lg:col-span-2 space-y-6">

          <h2 style={{ fontFamily: 'Cinzel, serif', color: '#C9A84C', fontSize: 18, fontWeight: 900, margin: 0 }}>
            Send a Message
          </h2>

          {/* Category selector */}
          <div>
            <label style={labelStyle}>Enquiry Category</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {CATEGORIES.map(cat => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setCategory(cat.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '8px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700,
                    cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s',
                    background: category === cat.id ? 'rgba(201,168,76,0.15)' : '#121E16',
                    border: category === cat.id ? '1px solid rgba(201,168,76,0.55)' : '1px solid #1C2E22',
                    color: category === cat.id ? '#C9A84C' : 'rgba(237,232,220,0.45)',
                  }}
                >
                  <span>{cat.icon}</span>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {cat.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Honeypot */}
            <input
              type="text" value={honeypot}
              onChange={e => setHoneypot(e.target.value)}
              style={{ display: 'none' }} tabIndex={-1}
              autoComplete="off" aria-hidden="true"
            />

            {/* Name + Email */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label style={labelStyle}>Full Name *</label>
                <input
                  type="text" value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Your full name"
                  style={errors.name ? inputErrorStyle : inputStyle}
                />
                {errors.name && <p style={{ color: '#ef4444', fontSize: 11, margin: 0 }}>{errors.name}</p>}
              </div>
              <div className="space-y-1">
                <label style={labelStyle}>Email Address *</label>
                <input
                  type="email" value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  style={errors.email ? inputErrorStyle : inputStyle}
                />
                {errors.email && <p style={{ color: '#ef4444', fontSize: 11, margin: 0 }}>{errors.email}</p>}
              </div>
            </div>

            {/* Phone + Subject */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label style={labelStyle}>Phone / WhatsApp (optional)</label>
                <input
                  type="tel" value={phone}
                  onChange={e => setPhone(e.target.value)}
                  placeholder="+234 800 000 0000"
                  style={inputStyle}
                />
              </div>
              <div className="space-y-1">
                <label style={labelStyle}>Subject *</label>
                <input
                  type="text" value={subject}
                  onChange={e => setSubject(e.target.value)}
                  placeholder={"Re: " + catConfig.label}
                  style={errors.subject ? inputErrorStyle : inputStyle}
                />
                {errors.subject && <p style={{ color: '#ef4444', fontSize: 11, margin: 0 }}>{errors.subject}</p>}
              </div>
            </div>

            {/* Message */}
            <div className="space-y-1">
              <label style={labelStyle}>Message *</label>
              <textarea
                rows={6} value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder={catConfig.placeholder}
                style={{ ...( errors.message ? inputErrorStyle : inputStyle ), resize: 'none' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {errors.message
                  ? <p style={{ color: '#ef4444', fontSize: 11, margin: 0 }}>{errors.message}</p>
                  : <span />}
                <p style={{ color: 'rgba(237,232,220,0.25)', fontSize: 11, margin: 0 }}>{message.length} chars</p>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={status === 'submitting'}
              style={{
                width: '100%', padding: '14px 0',
                background: status === 'submitting' ? 'rgba(201,168,76,0.5)' : '#C9A84C',
                border: 'none', borderRadius: 14, cursor: status === 'submitting' ? 'not-allowed' : 'pointer',
                color: '#070D0A', fontWeight: 900, fontSize: 14,
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                transition: 'background 0.2s',
              }}
            >
              {status === 'submitting' ? '⏳ Sending...' : catConfig.icon + ' Send Message'}
            </button>
          </form>
        </div>

        {/* ── Sidebar ─────────────────────────────────────────────── */}
        <div className="space-y-5">

          {/* Contact info */}
          <div style={{ background: '#0E1812', border: '1px solid #1C2E22', borderRadius: 16, padding: 24 }}>
            <h3 style={{ fontFamily: 'Cinzel, serif', color: '#C9A84C', fontSize: 15, fontWeight: 900, margin: '0 0 16px' }}>
              Contact Information
            </h3>
            <div className="space-y-4">
              {[
                { label: 'Secretariat Email', value: 'erueuzairue@gmail.com',  href: 'mailto:erueuzairue@gmail.com',           icon: '📧' },
                { label: 'Support Email',     value: 'support@erueuzairue.org', href: 'mailto:support@erueuzairue.org',         icon: '📮' },
                { label: 'WhatsApp',          value: '+234 705 7840 320',        href: 'https://wa.me/2347057840320',            icon: '💬' },
                { label: 'Website',           value: 'erueuzairue.org',          href: 'https://erueuzairue.org',               icon: '🌐' },
              ].map(c => (
                <div key={c.label}>
                  <div style={{ fontSize: 10, color: 'rgba(237,232,220,0.35)', textTransform: 'uppercase', fontWeight: 700, letterSpacing: 1, marginBottom: 2 }}>
                    {c.icon} {c.label}
                  </div>
                  <a href={c.href} target="_blank" rel="noreferrer"
                    style={{ color: '#C9A84C', fontSize: 13, textDecoration: 'none' }}>
                    {c.value}
                  </a>
                </div>
              ))}
            </div>
          </div>

          {/* Address */}
          <div style={{ background: '#0E1812', border: '1px solid #1C2E22', borderRadius: 16, padding: 24 }}>
            <h3 style={{ fontFamily: 'Cinzel, serif', color: '#C9A84C', fontSize: 15, fontWeight: 900, margin: '0 0 10px' }}>
              Address
            </h3>
            <address style={{ fontStyle: 'normal', fontSize: 13, color: 'rgba(237,232,220,0.55)', lineHeight: 1.8 }}>
              Palace of the Ogieneni of Uzairue Kingdom<br />
              Jattu-Uzairue<br />
              Etsako West Local Government Area<br />
              Edo State, Nigeria
            </address>
          </div>

          {/* Response times */}
          <div style={{ background: 'rgba(15,81,50,0.12)', border: '1px solid rgba(15,81,50,0.35)', borderRadius: 16, padding: 24 }}>
            <h3 style={{ color: '#6EE7A0', fontSize: 13, fontWeight: 900, margin: '0 0 8px' }}>
              ⏱ Response Times
            </h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }} className="space-y-1">
              {[
                'General: within 48 hours',
                'Sponsorship: within 24 hours',
                'Media / Press: within 12 hours',
                'Technical: within 6 hours',
              ].map(t => (
                <li key={t} style={{ fontSize: 12, color: 'rgba(237,232,220,0.5)' }}>• {t}</li>
              ))}
            </ul>
          </div>

          {/* Social links */}
          <div style={{ background: '#0E1812', border: '1px solid #1C2E22', borderRadius: 16, padding: 24, textAlign: 'center' }}>
            <p style={{ fontSize: 11, color: 'rgba(237,232,220,0.35)', marginBottom: 12 }}>
              Follow the ERUE Festival
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 10 }}>
              <a
                href="https://www.facebook.com/profile.php?id=61595036454360"
                target="_blank" rel="noreferrer"
                style={{
                  padding: '8px 16px', background: '#1877F2',
                  color: '#fff', fontWeight: 700, borderRadius: 8,
                  fontSize: 12, textDecoration: 'none',
                }}
              >Facebook</a>
              <a
                href="https://www.youtube.com/@ERUEFestivalofUzairueKingdom"
                target="_blank" rel="noreferrer"
                style={{
                  padding: '8px 16px', background: '#FF0000',
                  color: '#fff', fontWeight: 700, borderRadius: 8,
                  fontSize: 12, textDecoration: 'none',
                }}
              >YouTube</a>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default PublicContactForm;
